import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUsers } from '../../store/slices/userSlice';
import { fetchChannels } from '../../store/slices/channelSlice';
import { setCurrentChannel } from '../../store/slices/chatSlice';
import { fixMessageLinkHtml } from '../../utils/fileUtils';
import {
  enrichMentionHtml,
  findChannelByMention,
  findUserByMention,
  getMentionSegmentDisplayText,
  isEveryoneMention,
  MENTION_TAG_CLASS,
  parsePlainTextMentionSegments,
} from '../../utils/mentionDisplayUtils';
import { highlightSearchInHtml, renderHighlightedSearchText } from '../../utils/chatSearchUtils';
import PresignedAvatar from './PresignedAvatar';
import UserProfileModal from './UserProfileModal';

const MentionHoverCard = ({ anchorRect, children }) => {
  if (!anchorRect) return null;

  const top = anchorRect.bottom + 8;
  const left = Math.min(
    Math.max(anchorRect.left, 12),
    window.innerWidth - 280,
  );

  return ReactDOM.createPortal(
    <div
      className="fixed z-[10001] w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-3 pointer-events-none"
      style={{ top, left }}
    >
      {children}
    </div>,
    document.body,
  );
};

const UserMentionPreview = ({ user }) => (
  <div className="flex items-center gap-3">
    <PresignedAvatar
      fileKey={user.profilePicture}
      name={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
      className="w-10 h-10 rounded-full object-cover"
      fallbackClassName="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-medium"
    />
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
        {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.username}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">@{user.username}</p>
      {user.status && (
        <p className="text-[11px] text-primary-600 dark:text-primary-400 mt-0.5 capitalize">
          {String(user.status).replace(/_/g, ' ').toLowerCase()}
        </p>
      )}
    </div>
  </div>
);

const ChannelMentionPreview = ({ channel }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center text-sm font-bold">
      #
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
        {channel.name}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
        {channel.memberIds?.length || 0} member{(channel.memberIds?.length || 0) === 1 ? '' : 's'}
        {channel.isPrivate ? ' · Private' : ''}
      </p>
    </div>
  </div>
);

const MentionTag = ({ label, onMouseEnter, onMouseLeave, onClick }) => (
  <button
    type="button"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    className={`${MENTION_TAG_CLASS} inline align-baseline`}
  >
    {label}
  </button>
);

const MessageMentionContent = ({
  content,
  isRichText = false,
  normalizedContent,
  resolvedMessageUrl,
  className = '',
  style,
  renderTextSegment,
  mentionOnLight = false,
  searchHighlightQuery = '',
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const richRef = useRef(null);
  const { users = [] } = useSelector((state) => state.users);
  const { channels = [] } = useSelector((state) => state.channels);
  const [hoverTarget, setHoverTarget] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);

  useEffect(() => {
    if (!users.length) {
      dispatch(fetchUsers());
    }
    if (!channels.length) {
      dispatch(fetchChannels());
    }
  }, [dispatch, users.length, channels.length]);

  const resolveMention = useCallback((type, value) => {
    if (type === 'user') {
      if (isEveryoneMention(value)) {
        return {
          type,
          value,
          entity: {
            username: 'all',
            firstName: 'all',
            lastName: '',
            isEveryoneMention: true,
          },
        };
      }
      return { type, entity: findUserByMention(users, value), value };
    }
    return { type, entity: findChannelByMention(channels, value), value };
  }, [users, channels]);

  const handleMentionActivate = useCallback((type, value) => {
    if (type === 'user') {
      if (isEveryoneMention(value)) return;
      const user = findUserByMention(users, value);
      if (user?.id) {
        setProfileUserId(user.id);
      }
      return;
    }

    const channel = findChannelByMention(channels, value);
    if (channel) {
      dispatch(setCurrentChannel(channel));
      navigate('/chat');
    }
  }, [users, channels, dispatch, navigate]);

  const hoveredMentionRef = useRef(null);

  const showHoverForSegment = useCallback((type, value, element) => {
    if (!element || !type || !value) return;
    hoveredMentionRef.current = element;
    setHoverTarget({
      ...resolveMention(type, value),
      rect: element.getBoundingClientRect(),
    });
  }, [resolveMention]);

  const clearHoverPreview = useCallback(() => {
    hoveredMentionRef.current = null;
    setHoverTarget(null);
  }, []);

  const handleRichMouseOver = useCallback((event) => {
    const mentionEl = event.target.closest?.(`.${MENTION_TAG_CLASS}`);
    if (!mentionEl || !richRef.current?.contains(mentionEl)) {
      if (hoveredMentionRef.current) {
        clearHoverPreview();
      }
      return;
    }
    if (hoveredMentionRef.current === mentionEl) {
      return;
    }
    showHoverForSegment(
      mentionEl.dataset.mentionType,
      mentionEl.dataset.mentionValue,
      mentionEl,
    );
  }, [showHoverForSegment, clearHoverPreview]);

  const handleRichMouseOut = useCallback((event) => {
    const mentionEl = event.target.closest?.(`.${MENTION_TAG_CLASS}`);
    if (!mentionEl || !richRef.current?.contains(mentionEl)) {
      return;
    }

    const related = event.relatedTarget;
    if (related instanceof Node && mentionEl.contains(related)) {
      return;
    }
    if (related instanceof Element && related.closest?.(`.${MENTION_TAG_CLASS}`)) {
      return;
    }

    clearHoverPreview();
  }, [clearHoverPreview]);

  const handleRichContainerMouseLeave = useCallback(() => {
    clearHoverPreview();
  }, [clearHoverPreview]);

  const handleRichClick = useCallback((event) => {
    const mentionEl = event.target.closest?.(`.${MENTION_TAG_CLASS}`);
    if (!mentionEl || !richRef.current?.contains(mentionEl)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const type = mentionEl.dataset.mentionType;
    const value = mentionEl.dataset.mentionValue;
    if (type && value) {
      handleMentionActivate(type, value);
    }
  }, [handleMentionActivate]);

  const enrichedHtml = useMemo(() => {
    if (!isRichText) return '';
    const baseHtml = fixMessageLinkHtml(normalizedContent || content, resolvedMessageUrl);
    let html = enrichMentionHtml(baseHtml, { users, channels });
    if (searchHighlightQuery?.trim()) {
      html = highlightSearchInHtml(html, searchHighlightQuery);
    }
    return html;
  }, [isRichText, normalizedContent, content, resolvedMessageUrl, users, channels, searchHighlightQuery]);

  const plainSegments = useMemo(() => {
    if (isRichText) return [];
    return parsePlainTextMentionSegments(content || '', { users, channels });
  }, [isRichText, content, users, channels]);

  const hoverPreview = hoverTarget ? (
    <MentionHoverCard anchorRect={hoverTarget.rect}>
      {hoverTarget.type === 'user' && hoverTarget.entity?.isEveryoneMention ? (
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">@all</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Everyone in this channel
          </p>
        </div>
      ) : hoverTarget.type === 'user' && hoverTarget.entity ? (
        <UserMentionPreview user={hoverTarget.entity} />
      ) : hoverTarget.type === 'channel' && hoverTarget.entity ? (
        <ChannelMentionPreview channel={hoverTarget.entity} />
      ) : (
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {hoverTarget.type === 'user' ? `@${hoverTarget.value}` : `#${hoverTarget.value}`}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {hoverTarget.type === 'user' ? 'User not found' : 'Channel not found'}
          </p>
        </div>
      )}
    </MentionHoverCard>
  ) : null;

  const mentionContentClassName = [
    className,
    'workloop-mention-content',
    mentionOnLight ? 'workloop-mention-content--on-light' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {isRichText ? (
        <div
          ref={richRef}
          className={mentionContentClassName}
          style={style}
          onMouseOver={handleRichMouseOver}
          onMouseOut={handleRichMouseOut}
          onMouseLeave={handleRichContainerMouseLeave}
          onClick={handleRichClick}
          dangerouslySetInnerHTML={{ __html: enrichedHtml }}
        />
      ) : (
        <p
          className={mentionContentClassName}
          style={style}
          onMouseLeave={handleRichContainerMouseLeave}
        >
          {plainSegments.map((segment, index) => {
            if (segment.type === 'text') {
              const rendered = renderTextSegment
                ? renderTextSegment(segment.value)
                : (searchHighlightQuery?.trim()
                  ? renderHighlightedSearchText(segment.value, searchHighlightQuery, `text-${index}`)
                  : segment.value);
              return <React.Fragment key={`text-${index}`}>{rendered}</React.Fragment>;
            }

            return (
              <MentionTag
                key={`${segment.type}-${segment.value}-${index}`}
                label={getMentionSegmentDisplayText(segment, users, channels)}
                onMouseEnter={(event) => {
                  showHoverForSegment(segment.type, segment.value, event.currentTarget);
                }}
                onMouseLeave={(event) => {
                  const related = event.relatedTarget;
                  if (related instanceof Node && event.currentTarget.contains(related)) {
                    return;
                  }
                  clearHoverPreview();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleMentionActivate(segment.type, segment.value);
                }}
              />
            );
          })}
        </p>
      )}

      {hoverPreview}

      {profileUserId && (
        <UserProfileModal
          userId={profileUserId}
          isOpen={Boolean(profileUserId)}
          onClose={() => setProfileUserId(null)}
        />
      )}
    </>
  );
};

export default MessageMentionContent;
